import React from "react";
import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { useI18n } from "@/contexts/I18nContext";
import { ArrowLeft } from "lucide-react";

const COMPANY = {
  name: "TelcaVoIP International EU",
  address: "Ul. Zbozowa 4A, 70-653 Szczecin, Poland",
  vat: "PL8513242072",
  privacyEmail: "privacy@telcavoip.eu",
  supportEmail: "support@telcavoip.eu",
};

export default function Privacy() {
  const { t, locale } = useI18n();
  return (
    <div className="min-h-screen bg-white" data-testid="privacy-page">
      <Header />
      <article className="mx-auto max-w-3xl px-6 py-16 lg:px-10">
        <Link
          to="/"
          className="mb-8 inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#0B0E5C]"
          data-testid="privacy-back-link"
        >
          <ArrowLeft className="h-4 w-4" />
          {t("legal.back_home")}
        </Link>
        <p className="text-xs uppercase tracking-[0.2em] text-[#9ACD32] font-bold">
          {t("legal.privacy_last_updated")}
        </p>
        <h1 className="mt-3 font-display text-5xl font-extrabold tracking-tight text-[#05073B]">
          {t("legal.privacy_title")}
        </h1>
        <p className="mt-6 text-lg text-slate-600">{t("legal.privacy_intro")}</p>

        <ControllerCard locale={locale} />

        <div className="prose prose-slate mt-10 max-w-none text-[15px] leading-relaxed text-slate-700">
          {locale === "it" && <ItalianPrivacy />}
          {locale === "pl" && <PolishPrivacy />}
          {locale !== "it" && locale !== "pl" && <EnglishPrivacy />}
        </div>
      </article>
      <Footer />
    </div>
  );
}

function ControllerCard({ locale }) {
  const labels = {
    en: { title: "Data Controller", reg: "Registered office", vat: "VAT", contact: "Privacy contact" },
    it: { title: "Titolare del trattamento", reg: "Sede legale", vat: "Partita IVA", contact: "Contatto privacy" },
    pl: { title: "Administrator danych", reg: "Siedziba", vat: "NIP/VAT UE", contact: "Kontakt ws. prywatności" },
  };
  const l = labels[locale] || labels.en;
  return (
    <div className="mt-8 rounded-2xl border border-slate-200 bg-[#FAFAF8] p-6" data-testid="privacy-controller-card">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#9ACD32]">{l.title}</p>
      <h3 className="mt-2 font-display text-2xl font-bold text-[#05073B]">{COMPANY.name}</h3>
      <dl className="mt-4 grid gap-2 text-sm sm:grid-cols-3">
        <div>
          <dt className="text-xs uppercase tracking-wider text-slate-500">{l.reg}</dt>
          <dd className="mt-1 text-slate-700">{COMPANY.address}</dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wider text-slate-500">{l.vat}</dt>
          <dd className="mt-1 font-mono text-slate-700">{COMPANY.vat}</dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wider text-slate-500">{l.contact}</dt>
          <dd className="mt-1">
            <a
              href={`mailto:${COMPANY.privacyEmail}`}
              className="text-[#0B0E5C] underline-offset-4 hover:underline"
              data-testid="privacy-email-link"
            >
              {COMPANY.privacyEmail}
            </a>
          </dd>
        </div>
      </dl>
    </div>
  );
}

function EnglishPrivacy() {
  return (
    <>
      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-10 mb-3">1. Data we collect</h2>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>Account data:</strong> email, name, password hash (bcrypt), preferred language, optional referral code.</li>
        <li><strong>Usage data:</strong> messages sent, images generated, documents uploaded, code projects, current plan, billing history.</li>
        <li><strong>Content:</strong> chat history, generated images, code projects, uploaded PDFs and extracted text.</li>
        <li><strong>Technical:</strong> IP address, browser type, session tokens.</li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">2. Legal bases (GDPR Art. 6)</h2>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>Contract:</strong> processing necessary to provide the Service you subscribed to.</li>
        <li><strong>Legal obligation:</strong> billing/tax records retention.</li>
        <li><strong>Legitimate interest:</strong> abuse prevention, quota enforcement, fraud detection.</li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">3. How we use your data</h2>
      <ul className="list-disc pl-6 space-y-1">
        <li>To provide the Service (authentication, AI processing, billing).</li>
        <li>To enforce quotas and detect abuse.</li>
        <li>To comply with legal obligations (e.g. accounting records under Polish law).</li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">4. Third-party processors</h2>
      <p>Your prompts may be sent to AI providers for inference: Anthropic (Claude), OpenAI (GPT, GPT-Image-1), Google (Gemini, Nano Banana). Payments are processed by Stripe. Hosting is provided by our EU cloud infrastructure partner (EU regions where possible). Each processor is bound by a data-processing agreement compliant with GDPR Art. 28.</p>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">5. International transfers</h2>
      <p>Some AI providers (Anthropic, OpenAI, Google) process data in the United States. Transfers rely on EU Standard Contractual Clauses and additional technical safeguards (TLS encryption, minimisation).</p>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">6. GDPR rights</h2>
      <p>You have the right to: access, rectify, delete ({`"right to be forgotten"`}), restrict processing, object, and port your data. Contact us at <a href={`mailto:${COMPANY.privacyEmail}`} className="text-[#0B0E5C] underline-offset-4 hover:underline">{COMPANY.privacyEmail}</a>. You may also lodge a complaint with the Polish Personal Data Protection Office (UODO).</p>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">7. Data retention</h2>
      <ul className="list-disc pl-6 space-y-1">
        <li>Account data: kept while your account is active.</li>
        <li>AI conversation history & generated content: until you delete them (per-item or via account deletion).</li>
        <li>Billing records: 5 years from end of the accounting year (Polish Accounting Act).</li>
        <li>Server logs: 90 days.</li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">8. Cookies & local storage</h2>
      <p>We use one httpOnly session cookie for authentication. LocalStorage holds your language preference and JWT token. No third-party tracking or advertising cookies.</p>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">9. Security</h2>
      <p>All data is encrypted in transit (TLS 1.2+) and at rest. Passwords are bcrypt-hashed (cost ≥ 12). Access to production systems is limited to authorised personnel.</p>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">10. Children</h2>
      <p>The Service is not directed to children under 18. We do not knowingly collect personal data from minors.</p>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">11. Updates</h2>
      <p>We may update this Policy; the current version is always available on this page. Material changes will be notified by email at least 14 days in advance.</p>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">12. Contact</h2>
      <p>Privacy: <a href={`mailto:${COMPANY.privacyEmail}`} className="text-[#0B0E5C] underline-offset-4 hover:underline">{COMPANY.privacyEmail}</a> · Support: <a href={`mailto:${COMPANY.supportEmail}`} className="text-[#0B0E5C] underline-offset-4 hover:underline">{COMPANY.supportEmail}</a></p>
    </>
  );
}

function ItalianPrivacy() {
  return (
    <>
      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-10 mb-3">1. Dati raccolti</h2>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>Dati account:</strong> email, nome, hash della password (bcrypt), lingua preferita, codice referral opzionale.</li>
        <li><strong>Dati di utilizzo:</strong> messaggi inviati, immagini generate, documenti caricati, progetti code, piano attuale, storico pagamenti.</li>
        <li><strong>Contenuti:</strong> cronologia chat, immagini generate, progetti code, PDF caricati e testo estratto.</li>
        <li><strong>Dati tecnici:</strong> indirizzo IP, tipo browser, token di sessione.</li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">2. Basi giuridiche (Art. 6 GDPR)</h2>
      <ul className="list-disc pl-6 space-y-1">
        <li>{`Contratto: trattamento necessario all'erogazione del Servizio sottoscritto.`}</li>
        <li><strong>Obbligo legale:</strong> conservazione dei dati di fatturazione/fiscali.</li>
        <li><strong>Legittimo interesse:</strong> prevenzione abusi, applicazione delle quote, rilevamento frodi.</li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">3. Come usiamo i tuoi dati</h2>
      <ul className="list-disc pl-6 space-y-1">
        <li>Per erogare il Servizio (autenticazione, elaborazione AI, fatturazione).</li>
        <li>Per applicare le quote e rilevare abusi.</li>
        <li>Per adempiere a obblighi di legge (es. registrazioni contabili ai sensi del diritto polacco).</li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">4. Responsabili esterni del trattamento</h2>
      <p>{`I tuoi prompt possono essere inviati ai provider AI per l'inferenza: Anthropic (Claude), OpenAI (GPT, GPT-Image-1), Google (Gemini, Nano Banana). I pagamenti sono elaborati da Stripe. L'hosting è fornito dal nostro partner di infrastruttura cloud UE (regioni UE quando possibile). Ciascun responsabile è vincolato da un accordo di trattamento conforme all'art. 28 GDPR.`}</p>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">5. Trasferimenti internazionali</h2>
      <p>{`Alcuni provider AI (Anthropic, OpenAI, Google) trattano dati negli Stati Uniti. I trasferimenti si basano sulle Clausole Contrattuali Tipo dell'UE e su misure tecniche aggiuntive (crittografia TLS, minimizzazione).`}</p>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">6. Diritti GDPR</h2>
      <p>{`Hai diritto a: accesso, rettifica, cancellazione ("diritto all'oblio"), limitazione, opposizione, portabilità. Contattaci a `}<a href={`mailto:${COMPANY.privacyEmail}`} className="text-[#0B0E5C] underline-offset-4 hover:underline">{COMPANY.privacyEmail}</a>{`. Puoi inoltre presentare reclamo all'autorità polacca di protezione dei dati (UODO).`}</p>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">7. Conservazione dei dati</h2>
      <ul className="list-disc pl-6 space-y-1">
        <li>{`Dati account: conservati finché l'account è attivo.`}</li>
        <li>{`Cronologia AI e contenuti generati: fino a cancellazione (per singolo elemento o cancellando l'account).`}</li>
        <li>{`Registri di fatturazione: 5 anni dalla chiusura dell'esercizio (Legge polacca sulla contabilità).`}</li>
        <li>Log di sistema: 90 giorni.</li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">8. Cookie e localStorage</h2>
      <p>{`Usiamo un cookie di sessione httpOnly per l'autenticazione. Il localStorage conserva la preferenza di lingua e il token JWT. Nessun cookie di tracciamento o pubblicitario di terze parti.`}</p>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">9. Sicurezza</h2>
      <p>{`Tutti i dati sono crittografati in transito (TLS 1.2+) e a riposo. Le password sono hash con bcrypt (cost ≥ 12). L'accesso ai sistemi di produzione è limitato al personale autorizzato.`}</p>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">10. Minori</h2>
      <p>Il Servizio non è destinato ai minori di 18 anni. Non raccogliamo consapevolmente dati personali di minori.</p>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">11. Modifiche</h2>
      <p>Possiamo aggiornare la Policy; la versione corrente è sempre disponibile su questa pagina. Le modifiche sostanziali saranno notificate via email con almeno 14 giorni di preavviso.</p>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">12. Contatti</h2>
      <p>Privacy: <a href={`mailto:${COMPANY.privacyEmail}`} className="text-[#0B0E5C] underline-offset-4 hover:underline">{COMPANY.privacyEmail}</a> · Supporto: <a href={`mailto:${COMPANY.supportEmail}`} className="text-[#0B0E5C] underline-offset-4 hover:underline">{COMPANY.supportEmail}</a></p>
    </>
  );
}

function PolishPrivacy() {
  return (
    <>
      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-10 mb-3">1. Zbierane dane</h2>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>Dane konta:</strong> e-mail, imię, hash hasła (bcrypt), preferowany język, opcjonalny kod polecający.</li>
        <li><strong>Dane użytkowania:</strong> wysłane wiadomości, wygenerowane obrazy, wgrane dokumenty, projekty code, aktualny plan, historia płatności.</li>
        <li><strong>Treść:</strong> historia czatu, wygenerowane obrazy, projekty code, wgrane PDF i wyodrębniony tekst.</li>
        <li><strong>Dane techniczne:</strong> adres IP, typ przeglądarki, tokeny sesji.</li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">2. Podstawy prawne (art. 6 RODO)</h2>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>Umowa:</strong> przetwarzanie niezbędne do świadczenia Usługi.</li>
        <li><strong>Obowiązek prawny:</strong> przechowywanie dokumentów rozliczeniowych/podatkowych.</li>
        <li><strong>Uzasadniony interes:</strong> zapobieganie nadużyciom, egzekwowanie limitów, wykrywanie oszustw.</li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">3. Jak używamy danych</h2>
      <ul className="list-disc pl-6 space-y-1">
        <li>Do świadczenia Usługi (uwierzytelnianie, przetwarzanie AI, rozliczenia).</li>
        <li>Do egzekwowania limitów i wykrywania nadużyć.</li>
        <li>Do wypełniania obowiązków prawnych (ustawa o rachunkowości).</li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">4. Podmioty przetwarzające</h2>
      <p>Twoje prompty mogą być przesyłane do dostawców AI: Anthropic (Claude), OpenAI (GPT, GPT-Image-1), Google (Gemini, Nano Banana). Płatności obsługuje Stripe. Hosting zapewnia nasz partner infrastruktury chmurowej UE (regiony UE w miarę możliwości). Każdy podmiot jest związany umową powierzenia zgodną z art. 28 RODO.</p>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">5. Transfery międzynarodowe</h2>
      <p>Niektórzy dostawcy AI (Anthropic, OpenAI, Google) przetwarzają dane w USA. Transfery opierają się na Standardowych Klauzulach Umownych UE i dodatkowych zabezpieczeniach technicznych (szyfrowanie TLS, minimalizacja).</p>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">6. Prawa wynikające z RODO</h2>
      <p>{`Masz prawo do: dostępu, sprostowania, usunięcia ("prawo do bycia zapomnianym"), ograniczenia, sprzeciwu i przenoszenia danych. Kontakt: `}<a href={`mailto:${COMPANY.privacyEmail}`} className="text-[#0B0E5C] underline-offset-4 hover:underline">{COMPANY.privacyEmail}</a>{`. Możesz również złożyć skargę do Prezesa Urzędu Ochrony Danych Osobowych (UODO).`}</p>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">7. Okres przechowywania</h2>
      <ul className="list-disc pl-6 space-y-1">
        <li>Dane konta: do czasu likwidacji konta.</li>
        <li>Historia AI i wygenerowane treści: do usunięcia (pojedynczo lub przez usunięcie konta).</li>
        <li>Dokumenty rozliczeniowe: 5 lat od końca roku obrachunkowego (ustawa o rachunkowości).</li>
        <li>Logi serwera: 90 dni.</li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">8. Pliki cookie i localStorage</h2>
      <p>Używamy jednego cookie httpOnly do uwierzytelniania. LocalStorage przechowuje preferencję języka i token JWT. Brak cookies śledzących ani reklamowych stron trzecich.</p>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">9. Bezpieczeństwo</h2>
      <p>Wszystkie dane są szyfrowane w tranzycie (TLS 1.2+) i spoczynku. Hasła są haszowane bcrypt (cost ≥ 12). Dostęp do systemów produkcyjnych mają tylko upoważnione osoby.</p>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">10. Dzieci</h2>
      <p>Usługa nie jest skierowana do osób poniżej 18 roku życia. Świadomie nie zbieramy danych osobowych nieletnich.</p>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">11. Aktualizacje</h2>
      <p>Możemy aktualizować Politykę; aktualna wersja zawsze będzie dostępna na tej stronie. O istotnych zmianach poinformujemy e-mailem z co najmniej 14-dniowym wyprzedzeniem.</p>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">12. Kontakt</h2>
      <p>Prywatność: <a href={`mailto:${COMPANY.privacyEmail}`} className="text-[#0B0E5C] underline-offset-4 hover:underline">{COMPANY.privacyEmail}</a> · Wsparcie: <a href={`mailto:${COMPANY.supportEmail}`} className="text-[#0B0E5C] underline-offset-4 hover:underline">{COMPANY.supportEmail}</a></p>
    </>
  );
}
